import IconButton from "@/common/components/IconButton";
import { eventNames, shopWindowTypes, valueNames } from "@/modules/Constants";
import config from "@/modules/config";
import { findAskConfig, isPixieOpen } from "@/modules/pixieWorkspace";
import LinkData, { linkTypes } from "@/plugin/LinkData";
import type ShopWindowConfigList from "@/plugin/ShopWindowConfigList";
import ShopWindowRadioButtonBar from "@/components/shopwindow/ShopWindowRadioButtonBar";
import { pixieIntentProps } from "@/components/shopwindow/ask/pixieChunk";
import Share from "@/components/player/Share";
import type { ExperienceState } from "@/types/state";

import ChatAiIcon from "@svg/chat_ai.svg";
import CouponIcon from "@svg/coupon.svg";
import DonateIcon from "@svg/donate.svg";
import LeftIcon from "@svg/left.svg";
import LinkToIcon from "@svg/link_to.svg";
import RightIcon from "@svg/right.svg";

const REGION = "Control Bar";
const SECTION_CLASS =
  "control-bar-section tw:[.landscape_&]:flex tw:[.landscape_&]:w-full tw:[.landscape_&]:flex-col tw:[.portrait_&]:mr-[8px] tw:[.portrait_&]:flex tw:[.portrait_&]:h-full tw:[.portrait_&]:flex-row";

type ControlBarProps = {
  experienceState: ExperienceState;
  showPixieButton?: boolean;
  onApplicationEvent: (event: Record<string, unknown>) => void;
};

function controlBarLink(url: unknown, label: string) {
  return new LinkData(linkTypes.URL, url, "controlbar.cta", true, label);
}

function recipeTabCount(configs: ShopWindowConfigList | undefined) {
  if (!configs) {
    return 0;
  }
  let count = 0;
  for (let n = 0, max = configs.count; n < max; n++) {
    if (configs.atIndex(n).type !== shopWindowTypes.ASK) {
      count += 1;
    }
  }
  return count;
}

export default function ControlBar({
  experienceState,
  showPixieButton,
  onApplicationEvent,
}: ControlBarProps) {
  const applicationData = experienceState.applicationData;
  const experienceData = experienceState.experienceData;

  function onToggleShopWindow() {
    if (applicationData.showShopWindow()) {
      onApplicationEvent({
        name: eventNames.CLOSE_SHOP_WINDOW,
        region: REGION,
      });
    } else {
      onApplicationEvent({
        name: eventNames.OPEN_SHOP_WINDOW,
        region: REGION,
      });
    }
  }

  function onPixieClicked() {
    const askConfig = findAskConfig(applicationData);
    if (askConfig) {
      onApplicationEvent({
        name: eventNames.SET_SHOP_WINDOW,
        region: REGION,
        shopWindowConfig: askConfig,
      });
    }
  }

  function onCouponClicked() {
    onApplicationEvent({
      region: REGION,
      name: eventNames.LINK,
      link: controlBarLink(experienceData.couponUrl, "coupon"),
    });
  }

  function onDonateClicked() {
    onApplicationEvent({
      region: REGION,
      name: eventNames.LINK,
      link: controlBarLink(experienceData.donateUrl, "donate"),
    });
  }

  function onLinkToClicked() {
    onApplicationEvent({
      region: REGION,
      name: eventNames.LINK,
      link: controlBarLink(experienceData.linkToUrl, "link-to"),
    });
  }

  function getControlBarContent() {
    if (!experienceData.isLoaded()) {
      return null;
    }
    const configs = applicationData.getValue(
      valueNames.SHOP_WINDOW_CONFIG_LIST,
    ) as ShopWindowConfigList | undefined;
    if (configs && configs.count > 0) {
      return (
        <ShopWindowRadioButtonBar
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      );
    } else {
      return null;
    }
  }

  function getPixieButton() {
    if (!showPixieButton) {
      return null;
    }
    return (
      <IconButton
        className="pixie-rail-btn"
        onClick={onPixieClicked}
        {...pixieIntentProps}
        title="Pixie"
        icon={ChatAiIcon}
        iconName="chat-ai"
      />
    );
  }

  function getCouponButton() {
    if (experienceData.couponUrl) {
      return (
        <IconButton
          className="coupon-url tw:[.landscape_&]:mb-[5px]"
          onClick={onCouponClicked}
          title="Offers"
          icon={CouponIcon}
          iconName="coupon"
        />
      );
    }
  }

  function getDonateButton() {
    if (experienceData.donateUrl) {
      return (
        <IconButton
          className="donate-url tw:[.landscape_&]:mb-[5px]"
          onClick={onDonateClicked}
          title="Donate"
          icon={DonateIcon}
          iconName="donate"
        />
      );
    }
  }

  function getLinkToButton() {
    if (experienceData.linkToUrl) {
      return (
        <IconButton
          className="link-to-url tw:[.landscape_&]:mb-[5px]"
          onClick={onLinkToClicked}
          title="Stream"
          icon={LinkToIcon}
          iconName="link-to"
        />
      );
    }
  }

  const shareLink = config.shareLink();
  const share = shareLink ? (
    <Share link={shareLink} onApplicationEvent={onApplicationEvent} />
  ) : null;
  const configs = applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG_LIST,
  ) as ShopWindowConfigList | undefined;

  const tabCount = recipeTabCount(configs);
  const hasStackExtras =
    experienceData.couponUrl ||
    experienceData.donateUrl ||
    experienceData.linkToUrl;
  const hasSeparator = tabCount > 0 && hasStackExtras;
  const separator = hasSeparator ? (
    <div className="separator tw:bg-[rgba(200,200,200,0.5)] tw:[.landscape_&]:h-[1px] tw:[.landscape_&]:[margin:5px_10px_10px_10px] tw:[.portrait_&]:m-[10px] tw:[.portrait_&]:w-[1px]" />
  ) : null;

  return (
    <div className="control-bar tw:relative tw:flex tw:w-[55px] tw:bg-[#000328] tw:text-[#fff] tw:[.landscape_&]:flex-col tw:[.landscape_&]:items-stretch tw:[.landscape_&]:justify-between tw:[.portrait_&]:h-[45px] tw:[.portrait_&]:w-full tw:[.portrait_&]:flex-row tw:[.portrait_&]:justify-between">
      <div className={SECTION_CLASS}>
        {/* Pixie has its own close (X) and the Recipe | Pixie switch, so the
            chevron would be a third, unpredictable way out of it. */}
        {isPixieOpen(applicationData) ? null : (
          <button
            className="control-bar-toggle button type-4 hover centering tw:relative tw:[.landscape_&]:mt-[8px]"
            onClick={onToggleShopWindow}
            aria-label={
              applicationData.showShopWindow()
                ? "Hide recipe menu"
                : "Show recipe menu"
            }
          >
            {applicationData.showShopWindow() ? (
              <LeftIcon className="icon-left-d tw:pr-[2px]" />
            ) : (
              <RightIcon className="icon-right-d tw:pl-[2px]" />
            )}
          </button>
        )}
        {share}
      </div>
      <div className={SECTION_CLASS}>
        {getControlBarContent()}
        {getPixieButton()}
        {separator}
        {getDonateButton()}
        {getCouponButton()}
        {getLinkToButton()}
      </div>
    </div>
  );
}
